import type { Breadcrumb, Event } from "@sentry/react"

import { EMBED_URL_IDENTITY_PARAMS } from "@/common/services/chat/embed-url-identity-params"

/**
 * The embed widget's identity parameters, removed from every URL a Sentry event
 * or breadcrumb carries before it leaves the browser.
 *
 * 🔴 THE EMBED IFRAME'S OWN URL HOLDS THE VISITOR'S IDENTITY, `user_hash`
 * INCLUDED. `widget.js` writes `euid`, `user_hash`, `cemail`, `cphone` and the
 * rest into the iframe `src`, and `user_hash` is a permanent bearer credential
 * for that visitor. `@sentry/browser`'s `httpContextIntegration` copies
 * `location.href` into `event.request.url` — and the referrer into a `Referer`
 * header — on every error event AND every transaction, before `beforeSend` runs
 * (`helpers.js` `getHttpRequestData`, 10.27.0). Navigation breadcrumbs carry the
 * same URL as `from`/`to`. So without this every embed error shipped the
 * credential to Sentry.
 *
 * Only the identity parameters go, and their NAMES stay, so an event still says
 * which kind of embed load it came from: `?user_hash=…&lang=en` becomes
 * `?user_hash=%5BFiltered%5D&lang=en`. A URL carrying none of them is returned as
 * the same string, untouched.
 *
 * A server-side Sentry data-scrubbing rule is the backstop for whatever this
 * misses; it is configured in the Sentry org, not here.
 */

const FILTERED = "[Filtered]"

const IDENTITY_PARAMS: ReadonlySet<string> = new Set(EMBED_URL_IDENTITY_PARAMS)

/** Any base will do: it only has to make a relative URL parseable. Never emitted. */
const PARSE_BASE = "https://scrub.invalid"

/** `url` with every identity parameter's value replaced, or `url` itself when it has none. */
export function scrubEmbedIdentityFromUrl(url: string): string {
  if (!url.includes("?")) return url

  let parsed: URL
  try {
    parsed = new URL(url, PARSE_BASE)
  } catch {
    return url
  }

  const params = parsed.searchParams
  const hits = [...params.keys()].filter((key) => IDENTITY_PARAMS.has(key))
  if (hits.length === 0) return url

  for (const key of hits) params.set(key, FILTERED)

  // Rebuild only the query, so an absolute URL stays absolute and a relative one
  // stays relative — the base above is never written back.
  const queryStart = url.indexOf("?")
  const hashStart = url.indexOf("#", queryStart)
  const head = url.slice(0, queryStart)
  const fragment = hashStart === -1 ? "" : url.slice(hashStart)
  return `${head}?${params.toString()}${fragment}`
}

function scrubQueryString(query: NonNullable<NonNullable<Event["request"]>["query_string"]>) {
  if (typeof query === "string") {
    return scrubEmbedIdentityFromUrl(`?${query}`).slice(1)
  }
  if (Array.isArray(query)) {
    return query.map(([key, value]): [string, string] => [
      key,
      IDENTITY_PARAMS.has(key) ? FILTERED : value
    ])
  }
  const scrubbed: Record<string, string> = {}
  for (const [key, value] of Object.entries(query)) {
    scrubbed[key] = IDENTITY_PARAMS.has(key) ? FILTERED : value
  }
  return scrubbed
}

/** The breadcrumb `data` keys the browser SDK fills with a URL. */
const URL_DATA_KEYS: readonly string[] = ["url", "from", "to"]

/** `beforeBreadcrumb`'s step. Mutates and returns the breadcrumb. */
export function scrubEmbedIdentityFromBreadcrumb(breadcrumb: Breadcrumb): Breadcrumb {
  const data = breadcrumb.data
  if (data === undefined) return breadcrumb
  for (const key of URL_DATA_KEYS) {
    const value: unknown = data[key]
    if (typeof value === "string") data[key] = scrubEmbedIdentityFromUrl(value)
  }
  return breadcrumb
}

/**
 * `beforeSend` / `beforeSendTransaction`'s step: the request URL, its query
 * string, the referrer header and every breadcrumb. Mutates and returns the
 * event, which is the contract both hooks have.
 */
export function scrubEmbedIdentityFromEvent<T extends Event>(event: T): T {
  const request = event.request
  if (request !== undefined) {
    if (request.url !== undefined) request.url = scrubEmbedIdentityFromUrl(request.url)
    if (request.query_string !== undefined) {
      request.query_string = scrubQueryString(request.query_string)
    }
    const headers = request.headers
    if (headers !== undefined) {
      for (const [name, value] of Object.entries(headers)) {
        if (name.toLowerCase() === "referer" && typeof value === "string") {
          headers[name] = scrubEmbedIdentityFromUrl(value)
        }
      }
    }
  }
  event.breadcrumbs?.forEach(scrubEmbedIdentityFromBreadcrumb)
  return event
}
